/**
 * v7.99 — Version Guard: tablets de operação (cozinha/caixa/entregador)
 * ficam com a aba aberta 24/7 e NUNCA recebem o bundle novo após um
 * redeploy — foi a causa raiz do "Pronto na cozinha não chega ao
 * entregador" em produção (aparelho rodando código antigo).
 *
 * Estratégia SEGURA (sem loop — lição do v7.97):
 *  - Compara o hash do bundle carregado (main.<hash>.js do documento)
 *    com o do index.html publicado (fetch no-store) a cada 5 min.
 *  - Divergiu → auto-reload APENAS se: rota de operação (nunca em
 *    /delivery ou /rastreio), aparelho ocioso há 2+ min, e no MÁXIMO
 *    1 reload por hash novo (guard em localStorage). Caso contrário,
 *    dispara `kaven:newVersion` para o toast manual.
 *  - Em dev (bundle sem hash) fica 100% inerte.
 */

const RELOADED_KEY = 'kaven_vg_reloaded_for';
const CHECK_MS = 5 * 60 * 1000;
const IDLE_MS = 2 * 60 * 1000;

let started = false;
let lastInteraction = Date.now();

function currentBundleHash() {
  try {
    const scripts = document.querySelectorAll('script[src]');
    for (const s of scripts) {
      const m = (s.src || '').match(/\/static\/js\/main\.([a-f0-9]+)\.js/);
      if (m) return m[1];
    }
  } catch {}
  return null;
}

async function remoteBundleHash() {
  try {
    const r = await fetch(`/index.html?vg=${Date.now()}`, { cache: 'no-store' });
    if (!r.ok) return null;
    const html = await r.text();
    const m = html.match(/\/static\/js\/main\.([a-f0-9]+)\.js/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

function isClientRoute() {
  const p = window.location.pathname || '';
  return p.startsWith('/delivery') || p.startsWith('/rastreio');
}

/**
 * v8.01 — Hash do bundle em execução neste aparelho ('dev' em dev server).
 * Usado pela presença de dispositivos para o painel admin mostrar a versão.
 */
export function getCurrentBundleHash() {
  return currentBundleHash() || 'dev';
}

export function startVersionGuard() {
  if (started) return;
  started = true;
  const local = currentBundleHash();
  if (!local) return; // dev server — sem hash, guard inerte

  const markInteraction = () => { lastInteraction = Date.now(); };
  window.addEventListener('pointerdown', markInteraction, { passive: true });
  window.addEventListener('keydown', markInteraction, { passive: true });

  let notified = false;
  const check = async () => {
    const remote = await remoteBundleHash();
    if (!remote || remote === local) return;
    const idle = Date.now() - lastInteraction > IDLE_MS;
    let alreadyReloaded = null;
    try { alreadyReloaded = localStorage.getItem(RELOADED_KEY); } catch {}
    if (!isClientRoute() && idle && alreadyReloaded !== remote) {
      try { localStorage.setItem(RELOADED_KEY, remote); } catch {}
      // eslint-disable-next-line no-console
      console.log(`[VERSION GUARD] bundle ${local} → ${remote}: atualizando (1x por versão, aparelho ocioso)`);
      window.location.reload();
      return;
    }
    if (!notified) {
      notified = true;
      window.dispatchEvent(new CustomEvent('kaven:newVersion', { detail: { local, remote } }));
    }
  };

  setTimeout(check, 60000); // primeiro check só após o boot estabilizar
  setInterval(check, CHECK_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') setTimeout(check, 5000);
  });
  // v8.01 — reconexão de rede também dispara a checagem (tablets que
  // ficaram offline durante um deploy atualizam assim que a rede volta).
  window.addEventListener('online', () => setTimeout(check, 8000));
}
